// QueryClient padrão (Parte 14.2): sem retry em 4xx; erros de servidor tentam no máximo 2 vezes;
// todo erro de consulta/mutação vira toast com errorCode + correlationId (Parte 14.5) — exceto `step_up_required`,
// que abre o StepUpDialog (interceptor de step-up, REQ-AUTH-014) em vez de um toast.
import { GiyusApiError, GiyusNetworkError } from '@giyus/api-client';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isStepUpRequired, useStepUpStore } from '../auth/useStepUp';
import { useToastStore } from '../toast/toastStore';

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof GiyusApiError && error.status < 500) {
    return false;
  }
  return failureCount < 2;
}

export interface ErrorToastPayload {
  readonly errorCode: string | null;
  readonly correlationId: string | null;
  readonly serverTitle: string | null;
}

/** Extrai o que o toast precisa de qualquer erro (API, rede ou desconhecido). */
export function describeError(error: unknown): ErrorToastPayload {
  if (error instanceof GiyusApiError) {
    return {
      errorCode: error.errorCode,
      correlationId: error.correlationId ?? null,
      serverTitle: error.problem.title ?? null,
    };
  }
  if (error instanceof GiyusNetworkError) {
    return { errorCode: null, correlationId: error.correlationId ?? null, serverTitle: null };
  }
  return { errorCode: 'PLT_UNEXPECTED', correlationId: null, serverTitle: null };
}

export type ErrorNotifier = (payload: ErrorToastPayload) => void;

export const toastErrorNotifier: ErrorNotifier = (payload) => {
  useToastStore.getState().push({ kind: 'error', ...payload });
};

/** Interceptor de step-up: abre o diálogo (a ação é repetida pelo chamador via `useStepUpAction`). */
export type StepUpNotifier = () => void;

export const dialogStepUpNotifier: StepUpNotifier = () => {
  const store = useStepUpStore.getState();
  if (!store.open) {
    store.request(
      typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`,
    );
  }
};

export function createQueryClient(
  notify: ErrorNotifier = toastErrorNotifier,
  onStepUpRequired: StepUpNotifier = dialogStepUpNotifier,
): QueryClient {
  const handle = (error: unknown, silent: boolean): void => {
    if (isStepUpRequired(error)) {
      // Chamadas via `useStepUpAction` (silent) abrem o diálogo com a ação pendente; as demais só abrem o diálogo.
      if (!silent) {
        onStepUpRequired();
      }
      return;
    }
    if (!silent) {
      notify(describeError(error));
    }
  };
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Consultas com `meta.silent` tratam o erro localmente (ex.: ProblemAlert inline).
        handle(error, query.meta?.['silent'] === true);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        handle(error, mutation.meta?.['silent'] === true);
      },
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
